import React, { FC, useEffect, useState } from 'react';
import {
  Input,
  Sidebar,
  MinthrSelect,
  Button,
  Loader,
} from 'components/common';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import { t } from 'i18next';
import {
  DeleteFile,
  GetDetailsCategory,
  GetExpensesCatById,
  GetPayBy,
  UpdateOrCreateExpenseCat,
} from 'features/expenses/services/Expenses';
import { Formik, Form, FormikHelpers } from 'formik';
import '@fortawesome/fontawesome-free/css/all.min.css';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { ExpenseCat, ExpenseDetail } from 'features/expenses/types/type';
import { appendToFormData } from 'utils/globalMethodes';
import FileUpload from 'components/common/FileUpload';
import MinthrDateRangePicker from 'components/common/MinthrDateRangePicker';
import moment from 'moment';
import MintErrorMessage from 'components/common/MintErrorMessage';
import { useDialog } from 'contexts/DialogContext';
import { normalizeAmountForServer } from 'utils/numberFormatter';

interface NewExpenseProps {
  catId: number;
  expenseId?: number;
  noteId: number;
  toggleRefresh: () => void;
  toggleRefreshDT?: () => void;
  expenses?: ExpenseCat[];
}

interface NewExpenseValues {
  id: string;
  CatId: number;
  amount: number | string;
  category: string;
  date: string;
  description: string;
  distance: number | string;
  files: File[];
  fk_NoteId: number;
  payedBy: string;
  supplier: string;
  total?: number | string;
}

const NewExpense: FC<SideBarProps<NewExpenseProps>> = ({
  sidebar,
  setSidebarWidth,
  sidebarWidth,
  params,
}) => {
  const { closeSidebar } = useSidebarManager();
  const [payByOptions, setPayByOptions] = useState<
    {
      value: string | null;
      label: string | null;
    }[]
  >([]);
  const [model, setModel] = useState<ExpenseCat>();
  const [data, setData] = useState<ExpenseDetail>();
  const [loading, setLoading] = useState<boolean>(!!params);
  const [error, setError] = useState<string>('');
  const dialog = useDialog();
  const [filesNames, setFilesNames] = useState<FileType[]>([]);

  const handelDeleteFile = async (id: number): Promise<void> => {
    const confirmed = await dialog.delete(
      'File.delete_title',
      'File.delete_description',
    );

    if (confirmed) {
      const response = await DeleteFile(id);
      if (response.success) {
        toast.success(t('Default.succesMessage'));
        setFilesNames((prev) => [...prev.filter((item) => id != item.id)]);
      } else {
        toast.error(response.message);
      }
    }
  };

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await GetExpensesCatById(params.catId);
      if (res.success) {
        setModel(res.data);
      }
      if (params.expenseId) {
        const fetchedData = await GetDetailsCategory(params.expenseId);
        if (fetchedData.success) {
          setData(fetchedData.data);
          setFilesNames(fetchedData.data.filesNames);
        }
      }
      const response = await GetPayBy();
      if (response.success) {
        const formattedOptions = response.data.map((item: SelectItem) => ({
          value: item.value,
          label: item.text,
        }));
        setPayByOptions(formattedOptions);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetch();
  }, []);

  const validationSchema = Yup.object().shape({
    amount: Yup.string().required(t('RequiredAttribute')),
    payedBy:
      model?.type !== 'Mileage'
        ? Yup.string().required(t('RequiredAttribute'))
        : Yup.string().notRequired(),
  });

  const handleSubmit = async (
    values: NewExpenseValues,
    { setSubmitting }: FormikHelpers<NewExpenseValues>,
  ) => {
    const payload: NewExpenseValues = { ...values };

    if (payload.total) {
      payload.total = normalizeAmountForServer(payload.total);
    }

    if (payload.amount) {
      payload.amount = normalizeAmountForServer(payload.amount);
    }
    payload.distance = normalizeAmountForServer(payload.distance);

    const formData = appendToFormData(payload);
    try {
      const response = await UpdateOrCreateExpenseCat(formData);
      if (response.success) {
        closeSidebar(NewExpense);
        params.toggleRefresh();
        params?.toggleRefreshDT?.();
        toast.success(response.message);
      } else {
        // The API refuses with HTTP 200 + success: false (e.g. a required
        // attachment is missing): keep the form open and surface the message.
        const message =
          t(response.message) ||
          t(
            (response.validationErrors ?? [])
              .map((item) => item.message)
              .join(', '),
          ) ||
          t('Error.Generic', 'Une erreur est survenue');
        setError(message);
        toast.error(message);
      }
    } catch {
      toast.error(t('Error.Generic', 'Une erreur est survenue'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!model || loading)
    return (
      <Sidebar
        size="m"
        type="secondary"
        sidebar={sidebar}
        setSidebarWidth={setSidebarWidth}
        sidebarWidth={sidebarWidth}
      >
        <div className="minthr-flex minthr-flex-justify-center minthr-flex-items-center w-100 minthr-h-vh-100">
          <Loader />
        </div>
      </Sidebar>
    );

  return (
    <Sidebar
      size="m"
      type="secondary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
    >
      <div className="minthr-flex minthr-flex-items-center minthr-pb-2 minthr-mb-3 minthr-border-b minthr-border-b-3">
        <div
          className="minthr-icon-bg minthr-icon-bg-l minthr-icon-rounded minthr-mr-3"
          style={{ background: model?.bgColor || data?.expensesCat?.bgColor }}
        >
          <i
            className={model?.imgPath || data?.expensesCat?.imgPath}
            style={{ color: model?.imgColor || data?.expensesCat?.imgColor }}
          ></i>
        </div>
        <h3>{model?.name || data?.expensesCat?.name}</h3>
        <button
          className="minthr-sidebar-close"
          type="button"
          onClick={() => closeSidebar(NewExpense)}
        >
          <img src="/icons/close.svg" alt="close" />
        </button>
      </div>

      <Formik<NewExpenseValues>
        initialValues={{
          id: data ? data.id.toString() : '',
          date: data?.date
            ? moment(data?.date).format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD'),
          supplier: params.catId && data ? data.supplier || '' : '',
          amount: params.catId && data ? data.amount || 0 : model?.amount || 0,
          distance: params.catId && data ? data.distance : 0,
          description: params.catId && data ? data.description || '' : '',
          payedBy:
            params.catId && data && data.payedBy && data.payedBy !== 'none'
              ? data.payedBy
              : model?.type === 'Mileage'
                ? 'none'
                : '',
          fk_NoteId: params.noteId,
          category: model?.name || data?.expensesCat?.name || '',
          CatId: params.catId && params.catId ? params.catId : 0,
          files: [] as File[],
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ setFieldValue, values, isSubmitting }) => (
          <Form className="w-100">
            <div className="minthr-mt-4">
              {error && <MintErrorMessage message={error} />}
              {model?.helpComment && (
                <div className="help-message">
                  <ul>
                    <li>{model?.helpComment}</li>
                  </ul>
                </div>
              )}
              <div className="row g-0 minthr-mb-5">
                <div className="col-sm-12">
                  <MinthrDateRangePicker
                    label={t('date')}
                    startDateName="date"
                    singleDatePicker
                    onChange={(date) => setFieldValue('date', date)}
                    saveFormat="YYYY-MM-DD"
                  />
                </div>
              </div>
              {model.type !== 'Mileage' && model?.positive && (
                <div className="row g-0 minthr-mb-5">
                  <div className="col-sm-12">
                    <Input
                      name="supplier"
                      label={t('Expense.Supplier')}
                      type="text"
                      value={values.supplier || ''}
                    />
                  </div>
                </div>
              )}
              {!model.limited && (
                <div className="row g-0 minthr-mb-5">
                  <div className="col-sm-12">
                    <Input
                      name="amount"
                      label={t('Expense.Amount')}
                      type="number"
                      tooltip={[
                        model?.message,
                        model?.message2,
                        model?.message3,
                      ]
                        .filter(Boolean)
                        .join('\n')}
                    />
                  </div>
                </div>
              )}
              {model.limited && (
                <>
                  {model.expensesCatLimitsDto?.mileage ? (
                    <>
                      <div className="row g-0 minthr-mb-5">
                        <div className="col-sm-12">
                          <Input
                            name="distance"
                            label={`${t('Distance')} KM`}
                            type="number"
                            value={values.distance.toString()}
                            onChange={(e) => {
                              const newDistance = Number(e.target.value);
                              setFieldValue(
                                'total',
                                (newDistance * Number(values.amount)).toFixed(
                                  2,
                                ),
                              );
                            }}
                          />
                        </div>
                      </div>
                      <div className="row g-0 minthr-mb-5">
                        <div className="col-sm-12">
                          <Input
                            name="amount"
                            label={`${t('Expense.Amount')} MAD/KM`}
                            type="number"
                            value={values.amount.toString()}
                            tooltip={[
                              model?.message,
                              model?.message2,
                              model?.message3,
                            ]
                              .filter(Boolean)
                              .join('\n')}
                            IsDisabled={true}
                          />
                        </div>
                      </div>
                      <div className="row g-0 minthr-mb-5">
                        <div className="col-sm-12">
                          <Input
                            name="total"
                            label={t('Total')}
                            type="text"
                            value={(
                              Number(values.distance) * Number(values.amount)
                            ).toFixed(2)}
                            IsDisabled={true}
                          />
                        </div>
                      </div>
                    </>
                  ) : model.expensesCatLimitsDto?.type === 'fixed' ? (
                    <div className="row g-0 minthr-mb-5">
                      <div className="col-sm-12">
                        <Input
                          name="amount"
                          label={t('Expense.Amount')}
                          type="number"
                          value={values.amount.toString()}
                          IsDisabled={true}
                          tooltip={[
                            model?.message,
                            model?.message2,
                            model?.message3,
                          ]
                            .filter(Boolean)
                            .join('\n')}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="row g-0 minthr-mb-5">
                      <div className="col-sm-12">
                        <Input
                          name="amount"
                          label={t('Expense.Amount')}
                          type="number"
                          value={values.amount.toString()}
                          tooltip={[
                            model?.message,
                            model?.message2,
                            model?.message3,
                          ]
                            .filter(Boolean)
                            .join('\n')}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              {model.type !== 'Mileage' && payByOptions.length > 0 && (
                <div className="row g-0 minthr-mb-5">
                  <div className="col-sm-12">
                    <MinthrSelect
                      options={payByOptions}
                      name="payedBy"
                      label={t('Expense.PayedBy')}
                      className="minthr-form-outline"
                      value={values.payedBy}
                    />
                  </div>
                </div>
              )}
              <div className="row g-0 minthr-mb-5">
                <div className="col-sm-12">
                  <Input
                    name="description"
                    label={t('comment')}
                    type="text"
                    value={values.description || ''}
                  />
                </div>
              </div>
              <div className="row g-0 minthr-mb-5">
                {model.attachmentRequired && (
                  <div className="col-sm-12">
                    <small className="text-muted gray-color">
                      {t(
                        'Expenses.Attachment.Required',
                        'Une pièce jointe est obligatoire pour cette dépense.',
                      )}
                      <span style={{ color: 'red' }}> *</span>
                    </small>
                  </div>
                )}
                <div className="col-sm-12">
                  <FileUpload
                    name="files"
                    initialFiles={values.files || []}
                    allowedFileTypes={[
                      'png',
                      'jpg',
                      'jpeg',
                      'application/pdf',
                      'docx',
                      'doc',
                      'xls',
                      'xlsx',
                      'ppt',
                      'pptx',
                      'pdf',
                    ]}
                    existingFiles={filesNames}
                    onCustomDelete={handelDeleteFile}
                  />
                </div>
                {/* <div className="col-sm-12">
                  {filesNames.length > 0 &&
                    filesNames.map((file) => (
                      <a
                        key={file.id}
                        className="minthr-flex minthr-flex-items-center minthr-bg-light-gray minthr-text-decoration-none minthr-p-2 minthr-border-rounded minthr-mb-3"
                        id={`note-file-${file.id}`}
                      >
                        <img
                          className="minthr-icon-xl minthr-mr-2"
                          src="/icons/doc-file.svg"
                          alt=""
                        />
                        <div className="minthr-mr-6 minthr-pr-3 minthr-text-truncate">
                          <h6 title={file.name}>{file.name} </h6>
                          <div className="text-s"> mb</div>
                        </div>
                        <button
                          onClick={() => handelDeleteFile(file.id)}
                          type="button"
                          className="minthr-p-0 minthr-border-none minthr-bg-transparent minthr-ml-auto minthr-mr-2"
                        >
                          <img src="/icons/delete.svg" alt="" />
                        </button>
                      </a>
                    ))}
                </div> */}
              </div>
            </div>
            <div className="minthr-sidebar-footer minthr-box-shadow minthr-flex minthr-flex-items-center minthr-flex-items-end minthr-py-4 minthr-mx-n5 minthr-px-6 minthr-mt-auto">
              <button
                onClick={() => closeSidebar(NewExpense)}
                type="button"
                className="minthr-btn minthr-btn-outline minthr-btn-primary minthr-ml-auto minthr-sidebar-close"
              >
                {t('cancel')}
              </button>
              <Button
                type="submit"
                className="minthr-btn minthr-btn-primary minthr-ml-6 detail-depense-sidebar-open"
                loading={isSubmitting}
              >
                {t('submit')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Sidebar>
  );
};

export default NewExpense;
